import {
  DEFAULT_BRAND,
  DEFAULT_LOGO_URL,
  SUCCESS_COLOR,
  ERROR_COLOR,
} from "../constants";
import type { BrandingConfig } from "../types";

export type ResolvedBrand = {
  logo: string;
  name: string;
  primary: string;
  accent: string;
  primaryHover: string;
  primaryFocus: string;
  primaryText: string;
  text: string;
  border: string;
  background: string;
  borderRadius: string;
  fontFamily: string;
  successColor: string;
  errorColor: string;
};

const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

function normalizeHex(hex: string | undefined, fallback: string): string {
  if (!hex || !HEX_RE.test(hex.trim())) return fallback;
  return hex.trim();
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace("#", "").trim();
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

function darken(hex: string, amount: number): string {
  const { r, g, b } = hexToRgb(hex);
  const d = (c: number) => Math.max(0, Math.round(c * (1 - amount)));
  return `rgb(${d(r)}, ${d(g)}, ${d(b)})`;
}

function rgba(hex: string, alpha: number): string {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Relative luminance per WCAG; returns 0..1. */
function relativeLuminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  const norm = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * norm(r) + 0.7152 * norm(g) + 0.0722 * norm(b);
}

/** Returns "#ffffff" or "#050038" — whichever has better contrast on `bg`. */
export function contrastingTextOn(bg: string): string {
  return relativeLuminance(bg) > 0.55 ? "#050038" : "#ffffff";
}

export function resolveBranding(b?: BrandingConfig): ResolvedBrand {
  const primary = normalizeHex(b?.primaryColor, DEFAULT_BRAND.primaryColor);
  const accent = normalizeHex(
    b?.accentColor ?? b?.primaryColor,
    DEFAULT_BRAND.accentColor
  );
  return {
    logo: b?.logo || DEFAULT_LOGO_URL,
    name: b?.name || DEFAULT_BRAND.name,
    primary,
    accent,
    primaryHover: darken(primary, 0.08),
    primaryFocus: rgba(primary, 0.3),
    primaryText: contrastingTextOn(primary),
    text: b?.textColor || DEFAULT_BRAND.textColor,
    border: b?.borderColor || DEFAULT_BRAND.borderColor,
    background: b?.backgroundColor || DEFAULT_BRAND.backgroundColor,
    borderRadius: b?.borderRadius || DEFAULT_BRAND.borderRadius,
    fontFamily: b?.fontFamily || DEFAULT_BRAND.fontFamily,
    successColor: SUCCESS_COLOR,
    errorColor: ERROR_COLOR,
  };
}
